import { LIST_GC_TIME_MS, LIST_STALE_TIME_MS, useApiClient, useUtcToday } from '@klareospace/pharos-lib-front-utils';
import type { DriverTaskKpiRow, Kpi } from '@klareospace/pharos-lib-types';
import { isDriverTaskKpiRows } from '@klareospace/pharos-lib-types';
import { toUtcDayBound } from '@klareospace/pharos-lib-utils';
import { useQuery } from '@tanstack/react-query';

import { useMasterContext } from '&/master';

import { createKpisApi, kpiKeys } from '../api';
import { resolveKpiActiveRefetchInterval } from './resolve-kpi-refetch-interval';
import type { OrderKpisTodayScope } from './use-order-kpis-today';
import { useDriverKpisTodayRealtimeSync } from './use-driver-kpis-today-realtime-sync';
import { useIsKpiReadEnabled } from './use-kpi-read-access';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseDriverKpisTodayOptions {
  /** True while the home "Today" tab is active — enables a 60s heartbeat refetch once computed. */
  heartbeat?: boolean;
}

export interface UseDriverKpisTodayResult {
  /** True when the `feat-dashboard` flag is active AND the user holds `kpis:read`. */
  isEnabled: boolean;
  isError: boolean;
  isLoading: boolean;
  rows: DriverTaskKpiRow[] | undefined;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toDriverKpiRows(metrics: Kpi['metrics']): DriverTaskKpiRow[] | undefined {
  return isDriverTaskKpiRows(metrics) ? metrics : undefined;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Fetches today's `driver-task-kpis` KPI (UTC calendar day) for the current master-context scope —
 * gated behind the `feat-dashboard` flag and the `kpis:read` permission, mirrors `useOrderKpisToday`.
 */
export function useDriverKpisToday(options: UseDriverKpisTodayOptions = {}): UseDriverKpisTodayResult {
  const { heartbeat = false } = options;
  const client = useApiClient('deliveries');
  const api = createKpisApi(client);
  const { siteId, spaceId } = useMasterContext();
  const isEnabled = useIsKpiReadEnabled();
  const utcNow = useUtcToday();
  const bound = toUtcDayBound(utcNow);
  const scope: OrderKpisTodayScope = { siteId: siteId ?? undefined, spaceId: spaceId ?? undefined };

  const query = useQuery({
    enabled: isEnabled,
    gcTime: LIST_GC_TIME_MS,
    queryFn: (): Promise<Kpi> => api.getDriverKpisDay({ ...bound, ...scope }),
    queryKey: kpiKeys.driverDay({ ...bound, ...scope }),
    refetchInterval: resolveKpiActiveRefetchInterval(heartbeat),
    staleTime: LIST_STALE_TIME_MS,
  });

  useDriverKpisTodayRealtimeSync({ bound, enabled: isEnabled, scope });

  return {
    isEnabled,
    isError: query.isError,
    isLoading: query.isLoading,
    rows: toDriverKpiRows(query.data?.metrics),
  };
}
